import { Dispatch, useEffect, useMemo, useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import useAppDispatch from '../../../hooks/useAppDispatch';
import { useModal } from '../../../hooks/useModal';
import { configs } from '../../../constants/configs/configs';
import {
    getPasswordResetQuestions,
    validateResetPasswordQuestions,
    sendResetPasswordEmail,
} from '../../../actions/resetPassword/resetPasswordActionCreators';
import { SEND_EMAIL_ERROR_MODAL_KEY, SEND_EMAIL_MODAL_KEY } from '../modals/modalConfig';

interface IResetPassword {
    encHHId: string;
}

interface IRedirectData {
    url: string;
    email: string;
}

const STATUS = {
    OK: 'OK',
    ERROR: 'ERROR',
    ACCOUNT_LOCKED: 'ACCOUNT_LOCKED',
    ALLOW_RETRY: 'ALLOW_RETRY',
};

const useResetPassword = ({ encHHId }: IResetPassword) => {
    const intl = useIntl();
    const dispatch: Dispatch<any> = useAppDispatch();
    const { openModal, closeModal } = useModal();
    const [questions, setQuestions] = useState<string[]>([]);
    const [shouldShowQuestions, setShouldShowQuestions] = useState(false);
    const redirectDataDefaultValues = useMemo(
        () => ({
            url: `${configs.routes.RESET_PASSWORD_SUCCESS}?hhid=${encHHId}`,
            email: '',
        }),
        [encHHId],
    );
    const [redirectData, setRedirectData] = useState<IRedirectData>(redirectDataDefaultValues);
    const [shouldRedirect, setShouldRedirect] = useState<boolean>(false);
    const [hasError, setHasError] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>('');

    useEffect(() => {
        const fetchQuestions = async () => {
            setShouldShowQuestions(false);
            const { data, error, isSuccess } = await getPasswordResetQuestions(dispatch, encHHId);
            if (isSuccess) {
                if (data?.responseJson.STATUS === STATUS.OK) {
                    setQuestions(data.responseJson.QUESTIONS);
                    setShouldShowQuestions(true);
                } else if (isSuccess && data?.responseJson.STATUS === STATUS.ERROR) {
                    setHasError(true);
                    setErrorMessage(data?.responseJson?.ERROR_MESSAGE ?? '');
                } else if (isSuccess && data?.responseJson.STATUS === STATUS.ACCOUNT_LOCKED) {
                    setHasError(true);
                    setErrorMessage(intl.formatMessage({ id: 'resetPassword.message.accountLock' }));
                }
            } else {
                setHasError(true);
                setErrorMessage(error?.ERROR_MESSAGE ?? '');
            }
        };
        fetchQuestions();
    }, [intl]);

    const submitEmail = useCallback(
        async emailPayload => {
            const payload = {
                encHHId,
                emailPayload,
            };

            const { data, error, isSuccess } = await sendResetPasswordEmail(dispatch, payload);
            if (isSuccess) {
                if (data?.STATUS === STATUS.OK) {
                    const email = data?.EMAIL_ID ?? '';
                    setShouldRedirect(true);
                    setRedirectData({ ...redirectData, email });
                    closeModal(SEND_EMAIL_MODAL_KEY);
                } else {
                    const errorMessage =
                        data?.ERROR_MESSAGE ?? intl.formatMessage({ id: 'resetPassword.sendEmail.modal.error' });
                    openModal(SEND_EMAIL_ERROR_MODAL_KEY, { message: errorMessage });
                }
            } else {
                const errorMessage =
                    error?.ERROR_MESSAGE ?? intl.formatMessage({ id: 'resetPassword.sendEmail.modal.error' });
                openModal(SEND_EMAIL_ERROR_MODAL_KEY, { message: errorMessage });
            }
        },
        [dispatch, encHHId, redirectData],
    );

    const submitQuestionValidation = useCallback(
        async (answers: any) => {
            const payload = {
                encHHId,
                answers,
            };
            const { data, error, isSuccess } = await validateResetPasswordQuestions(dispatch, payload);
            if (isSuccess) {
                if (data?.STATUS === STATUS.OK) {
                    if (data.OVERRIDE_EMAIL) {
                        openModal(SEND_EMAIL_MODAL_KEY, { onSubmit: submitEmail });
                    } else {
                        const email = data.EMAIL_ID ?? '';
                        setShouldRedirect(true);
                        setRedirectData({ ...redirectData, email });
                    }
                } else if (data?.STATUS === STATUS.ALLOW_RETRY) {
                    setQuestions(data.QUESTIONS ?? []);
                    setShouldShowQuestions(true);
                    setHasError(true);
                    setErrorMessage(
                        intl.formatMessage(
                            { id: 'resetPassword.message.remainingRetries' },
                            { remainingRetries: data.REMAINING_RETRIES },
                        ),
                    );
                } else if (data?.STATUS === STATUS.ERROR) {
                    setShouldShowQuestions(false);
                    setHasError(true);
                    setErrorMessage(data?.ERROR_MESSAGE ?? '');
                } else {
                    setShouldShowQuestions(false);
                    setHasError(true);
                    setErrorMessage(intl.formatMessage({ id: 'resetPassword.message.accountLock' }));
                }
            } else {
                setHasError(true);
                setErrorMessage(error);
            }
        },
        [dispatch, encHHId, intl, openModal, submitEmail],
    );

    return {
        questions,
        shouldShowQuestions,
        submitQuestionValidation,
        submitEmail,
        shouldRedirect,
        redirectData,
        hasError,
        errorMessage,
    };
};

export default useResetPassword;
